import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, GripVertical, Layers } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { cn } from '../../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  baseTextSize,
} from '../../../../styling/stylingHelper';
import { RecipeComponent, RecipeStep, getTranslatedText, SupportedLanguage } from '../../../../services/recipes/recipesService';
import { RecipeNote } from '../../../../services/recipeNotes/recipeNotesService';
import RecipeComponentToggle from '../RecipeComponentToggle';
import { SortableList } from '../../../common/SortableList';

interface RecipeComponentsSectionProps {
  components: RecipeComponent[];
  getNotesForComponent: (componentId: string) => RecipeNote[];
  currentUserId: string;
  currentUserRole: 'admin' | 'staff';
  deletingNoteId: string | null;
  onReorderComponents: (reorderedComponents: RecipeComponent[]) => Promise<void>;
  onReorderSteps: (componentIndex: number, reorderedSteps: RecipeStep[]) => Promise<void>;
  onAddComponent: () => void;
  onEditComponent: (index: number) => void;
  onDeleteComponent: (index: number) => void;
  onAddNote: (componentId: string, componentDisplayName: string) => void;
  onDeleteNote: (noteId: string) => void;
}

interface SortableComponentRowProps {
  component: RecipeComponent;
  index: number;
  onReorderSteps: (componentIndex: number, reorderedSteps: RecipeStep[]) => Promise<void>;
  notes: RecipeNote[];
  currentUserId: string;
  currentUserRole: 'admin' | 'staff';
  onAddNote: () => void;
  onDeleteNote: (noteId: string) => void;
  deletingNoteId: string | null;
  onEditComponent: () => void;
  onDeleteComponent: () => void;
}

const SortableComponentRow = ({
  component,
  index,
  onReorderSteps,
  notes,
  currentUserId,
  currentUserRole,
  onAddNote,
  onDeleteNote,
  deletingNoteId,
  onEditComponent,
  onDeleteComponent,
}: SortableComponentRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: component.id || `component-${index}`,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <RecipeComponentToggle
        component={component}
        componentIndex={index}
        onReorderSteps={onReorderSteps}
        notes={notes}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
        onAddNote={onAddNote}
        onDeleteNote={onDeleteNote}
        deletingNoteId={deletingNoteId}
      />
      <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
        <button
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
          type="button"
        >
          <GripVertical className="w-5 h-5 text-[#98A2B3] dark:text-zinc-500" />
        </button>
      </div>
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button
          onClick={onEditComponent}
          className={cn(
            "p-2 rounded-lg",
            "bg-white dark:bg-zinc-800 border border-[#E5E7EB] dark:border-zinc-700",
            "text-[#344054] dark:text-zinc-300",
            "hover:bg-[#F9FAFB] dark:hover:bg-zinc-700 transition-colors"
          )}
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={onDeleteComponent}
          className={cn(
            "p-2 rounded-lg",
            "bg-[#FEF3F2] dark:bg-red-900/20",
            "text-[#B42318] dark:text-red-400",
            "hover:bg-[#FEE4E2] dark:hover:bg-red-900/30 transition-colors"
          )}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const RecipeComponentsSection = ({
  components,
  getNotesForComponent,
  currentUserId,
  currentUserRole,
  deletingNoteId,
  onReorderComponents,
  onReorderSteps,
  onAddComponent,
  onEditComponent,
  onDeleteComponent,
  onAddNote,
  onDeleteNote,
}: RecipeComponentsSectionProps) => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;

  const handleComponentsReorder = async (reordered: RecipeComponent[]) => {
    try {
      await onReorderComponents(reordered);
    } catch (error) {
      console.error('Failed to reorder components:', error);
    }
  };

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-[#667085] dark:text-textTertiary-dark" />
          <h2 className={cn("text-[18px] leading-6 font-semibold", primaryText)}>
            {t('recipes.components')}
          </h2>
          <span className={cn(
            "ml-2 px-2 py-0.5 rounded-md text-xs font-medium",
            "bg-[#F2F4F7] dark:bg-zinc-800 text-[#475467] dark:text-textTertiary-dark"
          )}>
            {components.length}
          </span>
        </div>
        <button
          onClick={onAddComponent}
          className={cn(
            "flex items-center gap-2 text-nowrap rounded-xl px-3 h-10",
            "text-sm font-semibold bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white",
            "hover:bg-[#047857] transition-colors"
          )}
        >
          <Plus className="w-4 h-4" />
          <span className="hidden min-[650px]:inline">{t('recipes.addComponent')}</span>
        </button>
      </div>

      {components.length === 0 ? (
        <div className={cn(
          "bg-white dark:bg-background-dark rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark",
          "p-8 text-center"
        )}>
          <div className="w-16 h-16 rounded-full bg-[#F2F4F7] dark:bg-zinc-800 flex items-center justify-center mx-auto mb-4">
            <Layers className="w-8 h-8 text-[#667085] dark:text-textTertiary-dark" />
          </div>
          <p className={cn(baseTextSize, semiBoldText, primaryText, 'mb-2')}>
            {t('recipes.noComponentsYet')}
          </p>
          <p className={cn(smallTextSize, secondaryText, 'mb-4')}>
            {t('recipes.addComponentDescription')}
          </p>
          <button
            onClick={onAddComponent}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl",
              "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white font-semibold",
              "hover:bg-[#047857] transition-colors"
            )}
          >
            <Plus className="w-5 h-5" />
            <span>{t('recipes.addFirstComponent')}</span>
          </button>
        </div>
      ) : (
        <SortableList
          items={components}
          getId={(c) => c.id || `component-${components.indexOf(c)}`}
          onReorder={handleComponentsReorder}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <div className="space-y-3">
            {components.map((component, index) => (
              <SortableComponentRow
                key={component.id || index}
                component={component}
                index={index}
                onReorderSteps={onReorderSteps}
                notes={getNotesForComponent(component.id)}
                currentUserId={currentUserId}
                currentUserRole={currentUserRole}
                onAddNote={() => onAddNote(component.id, getTranslatedText(component.nameTranslations, lang))}
                onDeleteNote={onDeleteNote}
                deletingNoteId={deletingNoteId}
                onEditComponent={() => onEditComponent(index)}
                onDeleteComponent={() => onDeleteComponent(index)}
              />
            ))}
          </div>
        </SortableList>
      )}
    </div>
  );
};

export default RecipeComponentsSection;
